import {NUMBER} from './data-types.js';
import {ZMESSAGE} from 'constants/object-types.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';

/**
 * Pattr system allows a patch to have various configurations
 * of presets
 */

export default class Pattr {
    constructor(subpatch) {
        this.subpatch = subpatch;

        // need a mapping from name => object
        this.nameToObject = {};

        this.hiddenObjects = [];

        this.parameters = [];
    }

    createParameters() {
        this.parameters = [];
        this.getOptionsAtLevel().filter(x => !this.hiddenObjects.includes(x))
            .forEach(o => this.createParameter(o));
    }

    createParameter(variable) {
        let context = this.getVariableContext(variable);
        let name = this.getFullVariableName(context === "" ? variable.id : context);
        this.parameters.push(new AbstractParameter(
            name,
            variable,
            (o, x) => {
                if (variable.getObjectType() === ZMESSAGE) {
                    // its a number object
                    variable.text = x;
                    variable.execute();
                } else {
                    // its a macro
                    variable.effect.setValue(x);
                }
            },
            {
                min: this.getVariableMin(variable),
                max: this.getVariableMax(variable),
                defaultValue: this.getVariableValue(variable),
            }
        ));
    }

    getVariableMin(variable) {
        return variable.effect ? 0 :
            variable.min;
    }

    getVariableMax(variable) {
        return variable.effect ? 0 :
            variable.max;
    }

    getVariableValue(variable) {
        return variable.effect ? variable.effect.value :
            variable.message;
    }

    getJson() {
        return {
            hiddenObjects: this.hiddenObjects.map(x => x.id),
            parameters: this.parameters.map(
                x => ({
                    name: x.name,
                    id: x.id,
                    min: x.min,
                    max: x.max,
                    depth: x.depth
                }))
        };
    }

    fromJson(json) {
        if (!json) {
            return;
        }

        let options = this.getOptionsAtLevel();
        this.hiddenObjects = json.hiddenObjects.map(
            id  => options.find(opt => opt.id === id))
            .filter(x => x);

        if (json.parameters && json.parameters.length > 0) {
            this.createParameters();
            for (let i=0; i < json.parameters.length; i++) {
                let param = this.parameters[i];
                if (param) {
                    param.id = json.parameters[i].id;
                    param.name = json.parameters[i].name;
                    if (json.parameters[i].min) {
                        param.min = json.parameters[i].min;
                        param.max = json.parameters[i].max;
                        param.depth = json.parameters[i].depth;
                    }
                }
            }
        }
    }

    hideObject(object) {
        this.hiddenObjects.push(object);
    }

    /**
     * Registers a variable to a name that describes what it does
     */
    registerPattr(name, object) {
        this.nameToObject[name] = object;
    }

    getValue(object) {
        if (object.getObjectType() === ZMESSAGE) {
            return object.message;
        }

        return object.effect ? object.effect.value : 0;
    }

    /**
     * Returns the full name (including subpatch path)
     */
    getFullVariableName(varName) {
        let subpatch = this.subpatch;
        let tree = [""];
        while (subpatch.getRootSubSequence) {
            if (subpatch.zObject) {
                tree.push(subpatch.zObject.args[0]);
            }
            subpatch = subpatch.subSequence;
        }
        tree.reverse();
        return tree.join("::") + varName;
    }

    /**
     * Returns all the options at this level of subpatch
     */
    getOptionsAtLevel() {
        let options = [
            ... this.subpatch.zMessages.filter(
                x => x.type === NUMBER),
            ... this.subpatch.zObjects.filter(
                x => x.name === "macro"),
            ];

        /**
        for (let zobject of this.subpatch.zObjects) {
            if (zobject.effect && zobject.effect.pattr) {
                options = [... options, ... zobject.effect.pattr.getAllOptions()];
            }
        }
        **/
        return options;
    }

    /**
     * Returns the pattr objects in all the subpatches inside this leve
     */
    getNextLevels() {
        return this.subpatch.zObjects.filter(
            x => x.effect && !x.effect.implements && x.effect.pattr)
            .map(x => x.effect.pattr);
    }

    /**
     * returns context of variable, so that we know about what its connected to
     */
    getVariableContext(variable) {
        let nodes = Object.values(variable.getOutboundNodes()).flat(2).map(
            x => x.node);
        let uniform = nodes.find(x => x.name === "gl.uniform");
        if (uniform) {
            return uniform.args[0];
        }
        let withName = nodes.find(x => x.name);
        return withName ? withName.name : "";
    }

    getNestedParameters() {
        let parameters = [... this.parameters];
        for (let pattr of this.getNextLevels()) {
            parameters = [
                ... parameters,
                ...pattr.getNestedParameters()];
        }
        return parameters;
    }
}
